import { SignIn, useAuth } from "@clerk/react";
import { useQueryClient } from "@tanstack/react-query";
import { Mail, MessageSquare } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { ApiError } from "@/api/client";
import { queryKeys, useRequestMagicLink, useRequestPhoneOTP, useVerifyPhoneOTP } from "@/api/hooks";
import { PhoneInput } from "@/components/PhoneInput";
import { CLERK_ENABLED } from "@/lib/clerk";

type Method = "email" | "phone";

/** Clerk-backed sign-in. The <SignIn> widget drives the whole flow
 * (email code, Google, Apple); once Clerk reports a session we
 * invalidate the cached /auth/me probe — which now carries the bearer
 * token via the api-client bridge — and bounce to wherever RequireAuth
 * originally intercepted the user. */
function ClerkLoginPage() {
  const { isSignedIn } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? "/";

  useEffect(() => {
    if (isSignedIn) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.me }).then(() => {
        navigate(from, { replace: true });
      });
    }
  }, [isSignedIn, queryClient, navigate, from]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-6 text-graphite">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <span className="brand-wordmark text-3xl">PencilOut</span>
          <p className="mt-3 text-sm text-graphite-soft">Sign in to continue.</p>
        </div>
        <div className="flex justify-center">
          <SignIn routing="hash" />
        </div>
      </div>
    </div>
  );
}

export function LoginPage() {
  if (CLERK_ENABLED) return <ClerkLoginPage />;
  return <LegacyLoginPage />;
}

function LegacyLoginPage() {
  const [method, setMethod] = useState<Method>("email");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [code, setCode] = useState("");
  /** UI sub-state for the email path: "form" → "sent" (check-inbox card). */
  const [emailSent, setEmailSent] = useState(false);
  /** UI sub-state for the phone path: "form" → "code" (OTP entry). */
  const [phoneCodeRequested, setPhoneCodeRequested] = useState(false);

  const requestMagicLink = useRequestMagicLink();
  const requestPhone = useRequestPhoneOTP();
  const verifyPhone = useVerifyPhoneOTP();
  const navigate = useNavigate();

  // Auto-submit the phone code once the user types 6 digits — same
  // UX as iOS PhoneOTPSheet.
  // biome-ignore lint/correctness/useExhaustiveDependencies: verifyPhone.mutateAsync identity changes on every render; .isPending flips inside the call. Including either re-runs this effect on a 6-digit code mid-flight and double-submits.
  useEffect(() => {
    if (phoneCodeRequested && code.length === 6 && /^\d{6}$/.test(code) && !verifyPhone.isPending) {
      verifyPhone
        .mutateAsync({ phone_number: phoneNumber, code })
        .then(() => {
          // Session cookie set by the mutation onSuccess; bounce
          // to the home page where RequireAuth re-checks /me.
          navigate("/");
        })
        .catch(() => {
          // Wrong code or expired — clear and let the user retype.
          setCode("");
        });
    }
  }, [code, phoneCodeRequested, phoneNumber, navigate]);

  async function onEmailSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!email.trim()) return;
    try {
      await requestMagicLink.mutateAsync({ email: email.trim() });
      setEmailSent(true);
    } catch {
      /* error surfaces via requestMagicLink.error */
    }
  }

  async function onPhoneSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmed = phoneNumber.trim();
    if (trimmed.replace(/\D/g, "").length < 10) return;
    try {
      await requestPhone.mutateAsync({ phone_number: trimmed });
      setPhoneCodeRequested(true);
    } catch {
      /* error surfaces via requestPhone.error */
    }
  }

  async function onPhoneVerifyManual(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (code.length !== 6) return;
    try {
      await verifyPhone.mutateAsync({
        phone_number: phoneNumber.trim(),
        code,
      });
      navigate("/");
    } catch {
      setCode("");
    }
  }

  function reset() {
    setEmailSent(false);
    setPhoneCodeRequested(false);
    setCode("");
    requestMagicLink.reset();
    requestPhone.reset();
    verifyPhone.reset();
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-6 text-graphite">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <span className="brand-wordmark text-3xl">PencilOut</span>
          <span className="label-mono ml-2">admin</span>
          <p className="mt-3 text-sm text-graphite-soft">
            {phoneCodeRequested
              ? "Enter the 6-digit code we just texted you."
              : emailSent
                ? "Tap the link in your inbox to finish signing in."
                : "Sign in with a one-time link or text."}
          </p>
        </div>

        {emailSent ? (
          <div className="rounded-lg border border-rule bg-paper-warm p-6 text-center">
            <Mail className="mx-auto h-6 w-6 text-pencil-yellow" aria-hidden />
            <h1 className="mt-3 font-mono text-lg font-bold tracking-tight">Check your inbox</h1>
            <p className="mt-2 text-sm text-graphite-soft">
              We sent a sign-in link to{" "}
              <span className="font-mono text-graphite">{email.trim()}</span>. The link is good for
              15 minutes.
            </p>
            <button
              type="button"
              onClick={reset}
              className="mt-4 font-mono text-xs text-graphite-soft underline hover:text-graphite"
            >
              Use a different email
            </button>
          </div>
        ) : phoneCodeRequested ? (
          <form
            onSubmit={onPhoneVerifyManual}
            className="space-y-4 rounded-lg border border-rule bg-paper-warm p-6"
          >
            <MessageSquare className="mx-auto h-6 w-6 text-pencil-yellow" aria-hidden />
            <p className="text-center text-sm text-graphite-soft">
              Sent to <span className="font-mono text-graphite">{phoneNumber.trim()}</span>.
            </p>
            <label className="block">
              <span className="label-mono">6-digit code</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                className="mt-1 w-full rounded border border-rule bg-paper px-3 py-3 text-center font-mono text-xl tracking-[0.5em] text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
              />
            </label>

            {verifyPhone.error && (
              <p className="text-sm text-red-pencil">
                {verifyPhone.error instanceof ApiError
                  ? verifyPhone.error.detail
                  : "Couldn't verify the code."}
              </p>
            )}

            <button
              type="submit"
              disabled={verifyPhone.isPending || code.length !== 6}
              className="w-full rounded bg-graphite px-3 py-2 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-50"
            >
              {verifyPhone.isPending ? "Verifying…" : "Verify"}
            </button>

            <div className="flex justify-between text-xs">
              <button
                type="button"
                onClick={() => {
                  void requestPhone.mutateAsync({ phone_number: phoneNumber.trim() });
                }}
                disabled={requestPhone.isPending}
                className="font-mono text-graphite-soft underline hover:text-graphite disabled:opacity-50"
              >
                {requestPhone.isPending ? "Sending…" : "Resend code"}
              </button>
              <button
                type="button"
                onClick={reset}
                className="font-mono text-graphite-soft underline hover:text-graphite"
              >
                Use a different number
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-4 rounded-lg border border-rule bg-paper-warm p-6">
            <div className="flex gap-1 rounded-md border border-rule bg-paper p-1">
              <button
                type="button"
                onClick={() => setMethod("email")}
                className={[
                  "flex-1 rounded px-3 py-1.5 font-mono text-xs transition",
                  method === "email"
                    ? "bg-graphite text-paper"
                    : "text-graphite-soft hover:text-graphite",
                ].join(" ")}
              >
                Email
              </button>
              <button
                type="button"
                onClick={() => setMethod("phone")}
                className={[
                  "flex-1 rounded px-3 py-1.5 font-mono text-xs transition",
                  method === "phone"
                    ? "bg-graphite text-paper"
                    : "text-graphite-soft hover:text-graphite",
                ].join(" ")}
              >
                Text
              </button>
            </div>

            {method === "email" ? (
              <form onSubmit={onEmailSubmit} className="space-y-4">
                <label className="block">
                  <span className="label-mono">Email</span>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="brad@pencilout.app"
                    className="mt-1 w-full rounded border border-rule bg-paper px-3 py-2 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
                  />
                </label>

                {requestMagicLink.error && (
                  <p className="text-sm text-red-pencil">
                    {requestMagicLink.error instanceof ApiError
                      ? requestMagicLink.error.detail
                      : "Could not send sign-in email."}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={requestMagicLink.isPending || !email.trim()}
                  className="w-full rounded bg-graphite px-3 py-2 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-50"
                >
                  {requestMagicLink.isPending ? "Sending…" : "Email me a sign-in link"}
                </button>
              </form>
            ) : (
              <form onSubmit={onPhoneSubmit} className="space-y-4">
                {/* biome-ignore lint/a11y/noLabelWithoutControl: <PhoneInput> renders the input internally as a descendant of this <label>; the DOM association works at runtime even though biome can't see through the component boundary. */}
                <label className="block">
                  <span className="label-mono">Phone</span>
                  <PhoneInput
                    autoFocus
                    required
                    value={phoneNumber}
                    onChange={setPhoneNumber}
                    placeholder="(704) 555-1234"
                    className="mt-1 w-full rounded border border-rule bg-paper px-3 py-2 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
                  />
                  <span className="mt-1 block text-xs text-graphite-soft">
                    We'll text you a 6-digit code.
                  </span>
                </label>

                {requestPhone.error && (
                  <p className="text-sm text-red-pencil">
                    {requestPhone.error instanceof ApiError
                      ? requestPhone.error.detail
                      : "Could not send the code."}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={requestPhone.isPending || phoneNumber.replace(/\D/g, "").length < 10}
                  className="w-full rounded bg-graphite px-3 py-2 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-50"
                >
                  {requestPhone.isPending ? "Sending…" : "Text me a code"}
                </button>
              </form>
            )}
          </div>
        )}

        <p className="mt-6 text-center font-mono text-xs text-graphite-soft">
          New here?{" "}
          <Link to="/signup" className="text-graphite underline hover:no-underline">
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
}
